/**
 * The text's level (handoff §9.1, §19.7): «Уровень текста», its level out of three,
 * the three steps with the current one marked, and what raises it. The level is the
 * text's own (ADR-116 Decision 4) and never falls; card levels are not touched by
 * practice. Read-only: starting practice is not on the screen yet.
 */
import { useLocale } from '../../i18n/context'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'

/** A text has three levels of its own. */
export const TEXT_LEVEL_MAX = 3

export default function TextLevelCard({ level, compact = false }: { level: number; compact?: boolean }) {
  const { tr } = useLocale()
  const ts = tr.textScreen
  const steps = [ts.levelStep1, ts.levelStep2, ts.levelStep3]
  return (
    <section
      data-testid="text-level-card"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      aria-label={`${ts.levelEyebrow}: ${tr.textList.levelAria(level, TEXT_LEVEL_MAX)}`}
      style={{
        padding: compact ? '12px 14px' : '14px 16px', borderRadius: 16, background: 'var(--bg-surface)',
        border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10,
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
        }}>
          {ts.levelEyebrow}
        </span>
        <span aria-hidden>
          <span style={{ fontFamily: 'var(--font-content)', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{level}</span>
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}> {tr.textList.levelOf(TEXT_LEVEL_MAX)}</span>
        </span>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((name, i) => {
          const current = i + 1 === level
          return (
            <li
              key={name}
              aria-current={current ? 'step' : undefined}
              data-testid={current ? 'text-level-current' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, minHeight: compact ? 30 : 32, padding: '2px 8px',
                borderRadius: 8, fontSize: 13, lineHeight: 1.3,
                background: current ? 'var(--accent-soft)' : 'transparent',
                color: current ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: current ? 600 : 400,
              }}
            >
              <span style={{ width: 12, textAlign: 'center', fontWeight: 600 }}>{i + 1}</span>
              <span style={{ flex: 1 }}>{name}</span>
              {current && (
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--accent)' }}>{ts.levelNow}</span>
              )}
            </li>
          )
        })}
      </ol>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{ts.levelFooter}</p>
    </section>
  )
}
